"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CUSTOMER_NOTIFICATION_SETTING_DEFINITIONS = void 0;
exports.getCustomerNotificationSettingDefinition = getCustomerNotificationSettingDefinition;
const notification_1 = require("./notification");
const COMMON_VARIABLES = [
    { key: "company_name", label: "Company name", sample: "CrewFSM Services" },
    {
        key: "customer_first_name",
        label: "Customer first name",
        sample: "Jordan",
    },
    {
        key: "customer_last_name",
        label: "Customer last name",
        sample: "Lee",
    },
    {
        key: "customer_display_name",
        label: "Customer display name",
        sample: "Jordan Lee Household",
    },
    {
        key: "service_address",
        label: "Service address",
        sample: "42 Bryant Park, New York, NY",
    },
];
const DEFAULT_CUSTOMER_DELIVERY = { email: false, sms: false };
function customer(input) {
    return notification_1.customerNotificationSettingSchema.parse({
        audience: "CUSTOMER",
        connected: input.connected ?? true,
        deliveryMethods: input.defaults ?? DEFAULT_CUSTOMER_DELIVERY,
        emailTemplate: {
            subject: input.subject,
            body: input.body,
            variables: [...COMMON_VARIABLES, ...(input.variables ?? [])],
        },
        defaultEmailTemplate: {
            subject: input.subject,
            body: input.body,
        },
        eventKey: input.eventKey,
        group: input.group,
        label: input.label,
        description: input.description,
    });
}
/**
 * Public defaults shared by the settings API and the worker. `connected`
 * deliberately reflects whether CrewFSM currently has a durable runtime
 * trigger for the event; unavailable lifecycle states are never presented as
 * functional configuration.
 */
exports.CUSTOMER_NOTIFICATION_SETTING_DEFINITIONS = [
    customer({
        eventKey: "JOB_SCHEDULED",
        group: "JOBS",
        label: "Scheduled",
        description: "Sent when a job is scheduled manually or through online booking.",
        subject: "Your service with {{company_name}} is scheduled",
        body: "Hi {{customer_first_name}},\n\nYour service at {{service_address}} has been scheduled.",
    }),
    customer({
        eventKey: "JOB_ON_MY_WAY",
        group: "JOBS",
        label: "On my way",
        description: "Sent when a technician is on their way to a job.",
        subject: "Your {{company_name}} technician is on the way",
        body: "Hi {{customer_first_name}},\n\nYour technician is on the way to {{service_address}}.",
    }),
    customer({
        eventKey: "JOB_FINISHED",
        group: "JOBS",
        label: "Finished",
        description: "Sent when a job is marked as finished.",
        subject: "Your service with {{company_name}} is complete",
        body: "Hi {{customer_first_name}},\n\nThank you for choosing {{company_name}}. Your service is complete.",
    }),
    customer({
        eventKey: "JOB_REMINDER",
        group: "JOBS",
        label: "Reminder",
        description: "Reminds the customer of an upcoming job appointment.",
        subject: "Reminder: upcoming service with {{company_name}}",
        body: "Hi {{customer_first_name}},\n\nThis is a reminder about your upcoming service at {{service_address}}.",
        connected: false,
    }),
    customer({
        eventKey: "JOB_CANCELED",
        group: "JOBS",
        label: "Canceled",
        description: "Sent when a job is canceled.",
        subject: "Service canceled by {{company_name}}",
        body: "Hi {{customer_first_name}},\n\nYour service at {{service_address}} has been canceled.",
    }),
    customer({
        eventKey: "ESTIMATE_SCHEDULED",
        group: "ESTIMATES",
        label: "Scheduled",
        description: "Sent when an estimate is scheduled.",
        subject: "Your estimate appointment with {{company_name}}",
        body: "Hi {{customer_first_name}},\n\nYour estimate appointment at {{service_address}} is scheduled.",
    }),
    customer({
        eventKey: "ESTIMATE_ON_MY_WAY",
        group: "ESTIMATES",
        label: "On my way",
        description: "Sent when a technician is on their way to an estimate.",
        subject: "Your {{company_name}} technician is on the way",
        body: "Hi {{customer_first_name}},\n\nYour technician is on the way for your estimate appointment.",
        connected: false,
    }),
    customer({
        eventKey: "ESTIMATE_FINISHED",
        group: "ESTIMATES",
        label: "Finished",
        description: "Sent when an estimate appointment is marked finished.",
        subject: "Your {{company_name}} estimate visit is complete",
        body: "Hi {{customer_first_name}},\n\nYour estimate visit is complete. We will follow up with the details.",
        connected: false,
    }),
    customer({
        eventKey: "ESTIMATE_SENT",
        group: "ESTIMATES",
        label: "Sent",
        description: "Sent when an estimate is delivered to the customer.",
        subject: "Estimate {{estimate_number}} from {{company_name}}",
        body: "Hi {{customer_first_name}},\n\nThank you for choosing {{company_name}}. Please review estimate {{estimate_number}} for {{total_estimate_value}}.",
        defaults: { email: true, sms: false },
        variables: [
            { key: "estimate_number", label: "Estimate number", sample: "1042" },
            {
                key: "total_estimate_value",
                label: "Total estimate value",
                sample: "$337.00",
            },
            {
                key: "estimate_option_value_low",
                label: "Estimate option value low",
                sample: "$300.00",
            },
            {
                key: "estimate_option_value_high",
                label: "Estimate option value high",
                sample: "$425.00",
            },
        ],
    }),
    customer({
        eventKey: "ESTIMATE_CANCELED",
        group: "ESTIMATES",
        label: "Canceled",
        description: "Sent when an estimate appointment is canceled.",
        subject: "Estimate appointment canceled",
        body: "Hi {{customer_first_name}},\n\nYour estimate appointment with {{company_name}} has been canceled.",
        connected: false,
    }),
    customer({
        eventKey: "INVOICE_SENT",
        group: "INVOICES",
        label: "Sent",
        description: "Sent when an invoice is delivered to the customer.",
        subject: "Invoice {{invoice_number}} from {{company_name}}",
        body: "Hi {{customer_first_name}},\n\nPlease review invoice {{invoice_number}} for {{invoice_total}}.",
        defaults: { email: true, sms: false },
        variables: [
            { key: "invoice_number", label: "Invoice number", sample: "2088" },
            { key: "invoice_total", label: "Invoice total", sample: "$337.00" },
        ],
    }),
    customer({
        eventKey: "INVOICE_REMINDER",
        group: "INVOICES",
        label: "Reminder",
        description: "Reminds the customer that an invoice is due.",
        subject: "Reminder: invoice {{invoice_number}} is due",
        body: "Hi {{customer_first_name}},\n\nThis is a reminder that invoice {{invoice_number}} from {{company_name}} is due.",
        variables: [
            { key: "invoice_number", label: "Invoice number", sample: "2088" },
        ],
        connected: false,
    }),
    customer({
        eventKey: "INVOICE_REFUND",
        group: "INVOICES",
        label: "Refund",
        description: "Sent when a verified refund is processed.",
        subject: "Refund from {{company_name}}",
        body: "Hi {{customer_first_name}},\n\nA refund of {{refund_amount}} has been processed for invoice {{invoice_number}}.",
        defaults: { email: true, sms: false },
        variables: [
            { key: "invoice_number", label: "Invoice number", sample: "2088" },
            { key: "refund_amount", label: "Refund amount", sample: "$125.00" },
        ],
    }),
    customer({
        eventKey: "INVOICE_RECEIPT",
        group: "INVOICES",
        label: "Receipt",
        description: "Sent when a customer payment is verified.",
        subject: "Payment receipt from {{company_name}}",
        body: "Hi {{customer_first_name}},\n\nWe received your payment of {{payment_amount}} for invoice {{invoice_number}}.",
        defaults: { email: true, sms: false },
        variables: [
            { key: "invoice_number", label: "Invoice number", sample: "2088" },
            { key: "payment_amount", label: "Payment amount", sample: "$337.00" },
        ],
    }),
];
function getCustomerNotificationSettingDefinition(eventKey) {
    const definition = exports.CUSTOMER_NOTIFICATION_SETTING_DEFINITIONS.find((item) => item.eventKey === eventKey);
    if (!definition) {
        throw new Error(`Customer notification definition not found: ${eventKey}`);
    }
    return definition;
}
//# sourceMappingURL=notification-settings.catalog.js.map